import React, { useEffect, useState } from 'react';
import styles from './TimeGateStep.module.css';

interface TimeGateStepProps {
  message: string;
  unlockAfterHours: number;
  storageKey: string;
  onSolved: () => void;
  initiallySolved: boolean;
  completionMessage?: string;
}

export const TimeGateStep: React.FC<TimeGateStepProps> = ({
  message,
  unlockAfterHours,
  storageKey,
  onSolved,
  initiallySolved,
  completionMessage,
}) => {
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isUnlocked, setIsUnlocked] = useState<boolean>(false);

  useEffect(() => {
    if (initiallySolved) {
      setIsUnlocked(true);
      return;
    }

    // Get the start time from localStorage
    const startTimeKey = `${storageKey}_startTime`;
    let startTime = localStorage.getItem(startTimeKey);
    
    if (!startTime) {
      // First time seeing this step, set the start time
      startTime = Date.now().toString();
      localStorage.setItem(startTimeKey, startTime);
    }

    const startTimestamp = parseInt(startTime);
    const unlockTime = startTimestamp + (unlockAfterHours * 60 * 60 * 1000);
    const now = Date.now();

    if (now >= unlockTime) {
      // Time has passed, unlock immediately
      setIsUnlocked(true);
      onSolved();
    } else {
      // Calculate remaining time
      const remaining = unlockTime - now;
      setTimeRemaining(remaining);

      // Set up countdown timer
      const timer = setInterval(() => {
        const currentRemaining = unlockTime - Date.now();
        if (currentRemaining <= 0) {
          setIsUnlocked(true);
          onSolved();
          clearInterval(timer);
        } else {
          setTimeRemaining(currentRemaining);
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [unlockAfterHours, storageKey, onSolved, initiallySolved]);

  const formatTime = (ms: number) => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const progress = unlockAfterHours > 0 ? 
    Math.max(0, Math.min(100, ((unlockAfterHours * 60 * 60 * 1000) - timeRemaining) / (unlockAfterHours * 60 * 60 * 1000) * 100)) : 0;

  // Render based on state - only one should be shown
  if (isUnlocked || initiallySolved) {
    return (
      <div className={styles.container}>
        <div className={styles.unlockedMessage}>
          <h3>✨ Time's up! ✨</h3>
          <p>{completionMessage || 'Ready to continue!'}</p>
        </div>
      </div>
    );
  }

  // Show waiting state
  return (
    <div className={styles.container}>
      <div className={styles.message}>
        <h3>{message}</h3>
      </div>
      
      <div className={styles.timerContainer}>
        <div className={styles.hourglass}>
          <div className={styles.hourglassTop}></div>
          <div className={styles.hourglassMiddle}></div>
          <div className={styles.hourglassBottom}></div>
        </div>
        
        <div className={styles.countdown}>
          <div className={styles.timeLabel}>Time remaining:</div>
          <div className={styles.timeDisplay}>{formatTime(timeRemaining)}</div>
        </div>
      </div>

      <div className={styles.progressContainer}>
        <div className={styles.progressBar}>
          <div 
            className={styles.progressFill} 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <div className={styles.progressText}>
          {Math.round(progress)}% complete
        </div>
      </div>

      <div className={styles.encouragement}>
        <p>⏳ The anticipation builds... ⏳</p>
        <p>Don't worry, The puzzle isn't going anywhere. 🤓</p>
      </div>
    </div>
  );
};

export default TimeGateStep;
